import { Box, Skeleton, Text, VStack } from "@chakra-ui/react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "@/shared/session";
import { PageContainer } from "@/ui/containers/PageContainer";
import { getErrorMessage } from "@/utils/getErrorMessage";
import { EmbeddedReport } from "@/core/reports/components/EmbeddedReport";
import { useGetCustomReportContent } from "../useCases/getCustomReportContent";
import { useGetCustomReports } from "../useCases/getCustomReports";

const CUSTOM_SANDBOX =
  "allow-scripts allow-popups allow-popups-to-escape-sandbox allow-modals";

const BREADCRUMB = [
  { label: "Análisis" },
  { label: "Reportes" },
  { label: "Personalizado" },
];

const ReportMessage = ({
  title,
  message,
  loading = false,
}: {
  title: string;
  message?: string;
  loading?: boolean;
}) => (
  <PageContainer title={title} breadcrumb={BREADCRUMB}>
    <Box
      minH="640px"
      bg="bg.panel"
      borderWidth="1px"
      borderColor="border"
      rounded="xl"
      shadow="xs"
      p={6}
    >
      {loading ? (
        <VStack gap={4} align="stretch" h="full" aria-live="polite">
          <Skeleton h="12" rounded="md" />
          <Skeleton h="8" w="40%" rounded="md" />
          <Skeleton h="96" rounded="md" />
          <Text fontSize="sm" color="fg.muted" textAlign="center">
            Cargando reporte…
          </Text>
        </VStack>
      ) : (
        <Text color="fg.muted" textAlign="center" py={20}>
          {message}
        </Text>
      )}
    </Box>
  </PageContainer>
);

export default function CustomReportPage() {
  const [searchParams] = useSearchParams();
  const { session } = useAuth();

  const id = searchParams.get("id");
  const url = searchParams.get("url");

  const { data: reports } = useGetCustomReports(session?.zone ?? "");
  const report = reports?.find((item) =>
    id ? item.id === id : item.url === url,
  );
  const title = report?.name ?? "Reporte personalizado";
  const description = report?.description || undefined;

  const { data, isPending, error } = useGetCustomReportContent(id);

  if (!id && !url) {
    return (
      <ReportMessage
        title={title}
        message="No se encontró la URL del reporte"
      />
    );
  }

  if (url) {
    return (
      <EmbeddedReport
        title={title}
        description={description}
        breadcrumb={BREADCRUMB}
        src={url}
        sandbox={CUSTOM_SANDBOX}
        autoHeight
      />
    );
  }

  if (isPending) {
    return <ReportMessage title={title} loading />;
  }

  if (error || !data) {
    return (
      <ReportMessage
        title={title}
        message={getErrorMessage(error, "No se pudo cargar el reporte")}
      />
    );
  }

  return (
    <EmbeddedReport
      title={title}
      description={description}
      breadcrumb={BREADCRUMB}
      html={data.html}
      sandbox={CUSTOM_SANDBOX}
      openUrl={null}
      autoHeight
    />
  );
}
